import { useEffect, useState } from 'react';
import { Offcanvas, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { StockControlDetailResult, StockControlRow } from '@econtractor/types';
import * as apiWarehouse from 'api/apiWarehouses';
import ESpinner from 'components/others/Spinner';
import TableEmptyRow from 'components/others/TableEmptyRow';
import CountBadge from '../../CounBadge';
import type { StockControlFilter } from '../model/stockControlFiltersStorage';

interface OffStockControlWarehousesProps {
    product: StockControlRow | null;
    filter: StockControlFilter;
    onHide: () => void;
}

const OffStockControlWarehouses = ({ product, filter, onHide }: OffStockControlWarehousesProps) => {
    const { t } = useTranslation(['Warehouses']);
    const [detail, setDetail] = useState<StockControlDetailResult | null>(null);

    useEffect(() => {
        if (!product?.productId) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        setDetail(null);
        const load = async () => {
            const res = await apiWarehouse.getStockControlDetail(product.productId, {
                includeMain: filter.includeMain,
                includeCustom: filter.includeCustom,
                includeTeam: filter.includeTeam,
                includeReserve: filter.includeReserve,
            }) as StockControlDetailResult | null;
            if (!cancelled) setDetail(res || { product: null, warehouses: [] });
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [
        product?.productId,
        filter.includeMain,
        filter.includeCustom,
        filter.includeTeam,
        filter.includeReserve,
    ]);

    const warehouses = detail?.warehouses || [];

    return (
        <Offcanvas
            className="offcanvas-bottom"
            placement="bottom"
            show={product !== null}
            onHide={onHide}
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>
                    {product?.prodName || t('stockControl.warehousesTitle')}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <div className="px-3 pb-3">
                    {detail ? (
                        <Table className="StockControlWarehousesTable table-hover">
                            <thead>
                                <tr className="bg-light">
                                    <td className="head-name">{t('stockControl.fields.warehouse')}</td>
                                    <td className="head-count">{t('fields.count')}</td>
                                </tr>
                            </thead>
                            <tbody>
                                {!warehouses.length ? (
                                    <TableEmptyRow colSpan={2} />
                                ) : (
                                    warehouses.map((row) => (
                                        <tr key={row.id}>
                                            <td className="name">{row.name}</td>
                                            <td className="count">
                                                <CountBadge fs={12} count={row.stockCount} />
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    ) : (
                        <ESpinner />
                    )}
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffStockControlWarehouses;
